import Button from 'components/UI/Button';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import Modal from 'components/UI/Modal';
import Pill from 'components/UI/Pill';
import Input from 'components/UI/Input';
import { SalesOrder } from 'data/SalesOrder';
import React, { useState } from 'react';
import styled from 'styled-components';
import { FontSize, FontWeight } from 'lib/styles';
import { Main, Text, Background, Border } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { SALES_ORDER_DETAIL_LABELS } from 'constants/salesOrderTypes';

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 70vh;
  max-height: 70vh;
`;

const ScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${Space.sp2x};
  background-color: ${Background.white};
`;

const SearchContainer = styled.div`
  flex-shrink: 0;
  background-color: ${Background.white};
  padding: 0 ${Space.sp2x};
  border-bottom: 1px solid ${Border.light};
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Space.sp2x};
`;

const SalesOrderCard = styled.div`
  border: 3px solid ${Main.brandLight};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${Main.brandDarkest};
    box-shadow: 0 4px 8px rgba(0,0,0,0.12);
  }
`;

const CardHeader = styled.div<{ $expanded: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Space.sp3x};
  cursor: pointer;
  border-radius: ${props => props.$expanded ? '8px 8px 0 0' : '8px'};
  background-color: ${Main.brandLight}
  
  &:hover {
    background-color: ${Background.light};
  }
`;

const CardHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Space.spBase};
  flex: 1;
`;

const CardTitle = styled.h3`
  font-size: ${FontSize.medium};
  font-weight: ${FontWeight.bold};
  color: ${Text.primary};
  margin: 0;
`;

const CardSubtitle = styled.div`
  font-size: ${FontSize.small};
  color: ${Text.secondary};
  display: flex;
  gap: ${Space.sp2x};
  align-items: center;
  flex-wrap: wrap;
`;

const CardHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${Space.sp2x};
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: ${Text.secondary};
  font-size: 14px;
  transition: transform 0.2s ease;
  transform: ${props => props.$expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const CardBody = styled.div<{ $expanded: boolean }>`
  display: ${props => props.$expanded ? 'block' : 'none'};
  border-top: 1px solid ${Border.light};
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${Space.sp3x};
  padding: ${Space.sp3x};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Space.sp2x};
`;

const GroupTitle = styled.h4`
  font-size: ${FontSize.small};
  font-weight: ${FontWeight.bold};
  color: ${Text.primary};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${Border.light};
  padding-bottom: ${Space.spBase};
`;

const FieldsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${Space.sp3x};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  color: ${Text.secondary};
  font-weight: ${FontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const FieldValue = styled.div`
  font-size: 14px;
  color: ${Text.primary};
  font-weight: ${FontWeight.medium};
  word-break: break-word;
  min-height: 20px;
`;

const EmptyValue = styled.span`
  color: ${Text.placeholder};
  font-style: italic;
  font-size: 12px;
`;

const HighlightText = styled.span`
  background-color: #fff3cd;
  padding: 1px 3px;
  border-radius: 2px;
  font-weight: ${FontWeight.bold};
`;

const NoResultsMessage = styled.div`
  text-align: center;
  color: ${Text.secondary};
  font-size: ${FontSize.medium};
  padding: ${Space.sp4x};
  font-style: italic;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${Space.spBase};
  align-items: center;
`;

const ExportButton = styled(Button)`
  font-size: 12px;
  padding: ${Space.spHalf} ${Space.spBase};
`;

const EntriesCount = styled.p`
  color: ${Text.secondary};
  font-size: 14px;
  font-weight: ${FontWeight.medium};
`;

const ModalFooter = styled.div`
  flex-shrink: 0;
  border-top: 1px solid ${Border.light};
  background-color: ${Background.white};
`;

const CardIndex = styled.div`
  background-color: ${Main.brandLight};
  color: ${Text.whitePrimary};
  font-size: 12px;
  font-weight: ${FontWeight.bold};
  padding: 4px 8px;
  border-radius: 12px;
  min-width: 24px;
  text-align: center;
`;

interface Props {
  salesOrderDetails: SalesOrder | SalesOrder[] | null;
  onClose: () => void;
  show: boolean;
}

// Utility functions
const formatDateFromYYYYMMDD = (dateStr: string): string => {
  if (!dateStr || dateStr === '00000000' || dateStr === '') {
    return 'Not set';
  }

  try {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    const date = new Date(`${year}-${month}-${day}`);

    if (isNaN(date.getTime())) {
      return dateStr;
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const formatTimeFromHHMMSS = (timeStr: string): string => {
  if (!timeStr || timeStr === '' || timeStr.length !== 6) {
    return timeStr;
  }

  try {
    const hours = timeStr.substring(0, 2);
    const minutes = timeStr.substring(2, 4);
    const seconds = timeStr.substring(4, 6);
    return `${hours}:${minutes}:${seconds}`;
  } catch {
    return timeStr;
  }
};

const formatValue = (key: string, value: any, docCurrency?: string): string => {
  if (value === null || value === undefined || value === '') {
    return 'Not available';
  }

  if (value === '00000000' || value === '0000000000') {
    return 'Not applicable';
  }

  // Handle objects by converting to meaningful string
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'Not available';
    }
    return JSON.stringify(value, null, 2);
  }

  // Format dates (YYYYMMDD format)
  if (key.toLowerCase().includes('date') && typeof value === 'string' && value.length === 8) {
    return formatDateFromYYYYMMDD(value);
  }

  // Format time (HHMMSS format)  
  if (key === 'createdTime' && typeof value === 'string' && value.length === 6) {
    return formatTimeFromHHMMSS(value);
  }

  // Format currency values
  if ((key === 'netValue' || key === 'tax') && docCurrency && value !== '') {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: docCurrency === 'CAD' ? 'CAD' : 'USD',
        minimumFractionDigits: 2
      });
      return formatter.format(numValue);
    }
  }

  return String(value);
};

const highlightSearchTerm = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm || !text) return text;

  const regex = new RegExp(`(${searchTerm})`, 'gi');
  const parts = text.toString().split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? <HighlightText key={index}>{part}</HighlightText> : part
  );
};

// Get human-readable label for any field
const getFieldLabel = (key: string): string => {
  return (SALES_ORDER_DETAIL_LABELS as Record<string, string>)[key] ||
    key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
};

// Group fields logically - Fixed field groups
const fieldGroups = {
  'Order Information': [
    'salesOrder', 'item', 'docType', 'jobStatus', 'createdOn', 'createdTime', 'createdBy'
  ],
  'Customer Details': [
    'soldTo', 'shipTo', 'billTo', 'customer_purchase_no', 'customerContract'
  ],
  'Financial Information': [
    'netValue', 'tax', 'docCurrency', 'paymentTerms', 'priceBookName', 'profitCenter', 'revenueType'
  ],
  'Delivery & Status': [
    'deliveryStatus', 'orderRejectionStatus', 'requestDeliveryDate', 'scheduledDeliveryDate', 'schduledDeliveryDate',
    'orderQuantity', 'scheduledDeliveryConfirmedQty', 'deliveryBlock', 'billingBlock'
  ],
  'Material Details': [
    'materialNumber', 'materialDescription', 'localMaterialNo', 'localMaterialDesc',
    'plant', 'localUoM', 'salesUnit'
  ],
  'Project Information': [
    'project', 'pamProject', 'basin', 'geoMarket', 'subGeo', 'segment', 'subSeg',
    'wellName', 'rigName', 'jobClass'
  ],
  'Business Terms': [
    'incoTerms1', 'incoTerms2', 'contractStartDate', 'contractEndDate',
    'acceptanceDate', 'jobStartDate', 'jobEndDate'
  ],
  'Administrative': [
    'operationalApprover', 'performingOrg', 'managementCountry', 'salesOrg',
    'distributionChannel', 'division', 'poType'
  ],
  'Technical Details': [
    'sourceId', 'integrationId', 'SOItemGUID', 'wbsElement', 'reason4Rejection',
    'higherLevelItem', 'quoteNumber'
  ]
};

const SalesOrderDetailsModal: React.FC<Props> = ({
  salesOrderDetails,
  onClose,
  show
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  if (!salesOrderDetails) {
    return null;
  }

  const salesOrderArray = Array.isArray(salesOrderDetails) ? salesOrderDetails : [salesOrderDetails];

  // Filter entries based on search term
  const filteredEntries = salesOrderArray.filter(item => {
    if (!searchTerm) return true;

    return Object.entries(item).some(([key, value]) => {
      const label = getFieldLabel(key);
      return (
        label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (value && value.toString().toLowerCase().includes(searchTerm.toLowerCase()))
      );
    });
  });

  const toggleCard = (cardId: string) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(cardId)) {
      newExpanded.delete(cardId);
    } else {
      newExpanded.add(cardId);
    }
    setExpandedCards(newExpanded);
  };

  const exportAllToJSON = () => {
    const dataStr = JSON.stringify(filteredEntries, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `sales_orders_${filteredEntries.length}_entries.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Get all unique keys from all entries to ensure we don't miss any fields
  const allKeys = Array.from(new Set(
    salesOrderArray.flatMap(item => Object.keys(item))
  ));

  // Group all keys by their categories
  const organizedFields: { [groupName: string]: string[] } = {};

  // First, add known grouped fields
  Object.entries(fieldGroups).forEach(([groupName, fields]) => {
    const existingFields = fields.filter(field => allKeys.includes(field));
    if (existingFields.length > 0) {
      organizedFields[groupName] = existingFields;
    }
  });

  // Then add any remaining fields that weren't categorized
  const categorizedFields = Object.values(fieldGroups).flat();
  const uncategorizedFields = allKeys.filter(key => !categorizedFields.includes(key));

  if (uncategorizedFields.length > 0) {
    organizedFields['Other Fields'] = uncategorizedFields;
  }

  return (
    <Modal
      width="60%"
      left='25vw'
      showModal={show}
      onClose={onClose}
      title="Sales Order Details"
      showButtons={false}
      modalFooterMargin="0"
    >
      <ModalContainer>
        {/* Search and Actions - Fixed at top */}
        <SearchContainer>
          <FlexboxContainerComponent alignItems="center" justifyContent="space-between">
            <FlexboxContainerComponent alignItems="center" gap={Space.sp2x}>
              <Input
                id="search-input"
                type="text"
                placeholder="Search across all entries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <EntriesCount>
                Showing {filteredEntries.length} of {salesOrderArray.length} entries
              </EntriesCount>
            </FlexboxContainerComponent>
            <HeaderActions>
              <ExportButton
                size="s"
                color={Text.primary}
                bgColor={Main.lightGray}
                onClick={exportAllToJSON}
              >
                Export All ({filteredEntries.length})
              </ExportButton>
            </HeaderActions>
          </FlexboxContainerComponent>
        </SearchContainer>

        {/* Scrollable Content Area */}
        <ScrollContainer>
          {/* Sales Order Cards */}
          {filteredEntries.length === 0 ? (
            <NoResultsMessage>
              No sales orders match your search criteria
            </NoResultsMessage>
          ) : (
            <CardsContainer>
              {filteredEntries.map((item, index) => {
                const originalIndex = salesOrderArray.findIndex(original => original === item);
                const cardId = `${item.salesOrder}-${item.item}-${index}`;
                const isExpanded = expandedCards.has(cardId);

                return (
                  <SalesOrderCard key={cardId}>
                    <CardHeader
                      $expanded={isExpanded}
                      onClick={() => toggleCard(cardId)}
                    >
                      <CardHeaderLeft>
                        <CardTitle>
                          {highlightSearchTerm(item.salesOrder || 'N/A', searchTerm)} - {highlightSearchTerm(item.item || 'N/A', searchTerm)}
                        </CardTitle>
                        <CardSubtitle>
                          <span>{item.materialDescription || 'No description'}</span>
                          {item.jobStatus && (
                            <Pill theme={'success'} size="m" margin="0 0 16px 16px">
                              {item.jobStatus}
                            </Pill>
                          )}
                          {item.netValue && (
                            <span style={{ fontWeight: FontWeight.bold }}>
                              {formatValue('netValue', item.netValue, item.docCurrency)}
                            </span>
                          )}
                        </CardSubtitle>
                      </CardHeaderLeft>
                      <CardHeaderRight>
                        <CardIndex>#{originalIndex + 1}</CardIndex>
                        <ExpandIcon $expanded={isExpanded}>▼</ExpandIcon>
                      </CardHeaderRight>
                    </CardHeader>

                    <CardBody $expanded={isExpanded}>
                      <FieldsGrid>
                        {Object.entries(organizedFields).map(([groupName, fields]) => {
                          // Only show groups that have fields with values or match search
                          const visibleFields = fields.filter(field => {
                            const value = item[field as keyof SalesOrder];

                            if (!searchTerm) {
                              // Show field if it has any value (including 0, false, empty string)
                              return value !== undefined && value !== null;
                            }

                            const label = getFieldLabel(field);

                            return (
                              label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              (value !== null && value !== undefined &&
                                value.toString().toLowerCase().includes(searchTerm.toLowerCase()))
                            );
                          });

                          if (visibleFields.length === 0) return null;

                          return (
                            <FieldGroup key={groupName}>
                              <GroupTitle>{highlightSearchTerm(groupName, searchTerm)}</GroupTitle>
                              <FieldsRow>
                                {visibleFields.map(field => {
                                  const label = getFieldLabel(field);
                                  const value = item[field as keyof SalesOrder];
                                  const formattedValue = formatValue(field, value, item.docCurrency);

                                  return (
                                    <FieldItem key={field}>
                                      <FieldLabel>{highlightSearchTerm(label, searchTerm)}</FieldLabel>
                                      <FieldValue>
                                        {formattedValue === 'Not available' || formattedValue === 'Not applicable' || formattedValue === 'Not set' ? (
                                          <EmptyValue>{highlightSearchTerm(formattedValue, searchTerm)}</EmptyValue>
                                        ) : (
                                          highlightSearchTerm(formattedValue, searchTerm)
                                        )}
                                      </FieldValue>
                                    </FieldItem>
                                  );
                                })}
                              </FieldsRow>
                            </FieldGroup>
                          );
                        })}
                      </FieldsGrid>
                    </CardBody>
                  </SalesOrderCard>
                );
              })}
            </CardsContainer>
          )}
        </ScrollContainer>

        {/* Fixed Footer */}
        <ModalFooter>
          <FlexboxContainerComponent
            justifyContent="flex-end"
            padding={`${Space.sp2x} ${Space.sp3x}`}
          >
            <Button
              id="modal-close-button"
              color={Text.whitePrimary}
              bgColor={Background.navy}
              onClick={onClose}
            >
              Close
            </Button>
          </FlexboxContainerComponent>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};

export default SalesOrderDetailsModal;